import { FC, useState } from 'react'
import logo from '../../assets/deliveroo/logo.jpg'
import {
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Grid,
} from '@mui/material'
import {
  useClientApiCall,
  useClientApiMutation,
} from '../../hooks/useClientApiCall'
import { useSearchParams } from 'react-router-dom'
import { ApplicationLoadingScreen } from '../../components/ApplicationLoadingScreen'
import { toast } from 'react-toastify'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import InfoIcon from '@mui/icons-material/Info'

/**
 * Deliveroo Application
 */
export const DeliverooMarketplace: FC = () => {
  const [params] = useSearchParams()
  const session = params.get('session')!
  const connection = useClientApiCall(session, 'connection')
  const validateIntegration = useClientApiMutation(
    session,
    'validate-integration'
  )
  const [isValidating, setIsValidating] = useState(false)

  if (connection.isLoading) {
    return <ApplicationLoadingScreen />
  }

  const connectionData = connection.data as any
  const isConnected = connectionData?.connection?.deliverooBrandId

  // Get location ID from session token (this is the correct way)
  const locationId = connectionData?.locationId || ''

  return (
    <Grid
      container
      component="main"
      sx={{ height: '100vh', overflowY: 'auto', overflowX: 'hidden' }}
    >
      {/* Left Panel with Perfectly Centered Logo - Deliveroo Specific */}
      <Grid
        item
        xs={false}
        sm={5}
        md={8}
        sx={{
          backgroundColor: '#04cdbe',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center', // Perfect vertical centering
        }}
      >
        <Box
          sx={{
            width: '50%',
            height: '50%',
            backgroundImage: `url(${logo})`,
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'contain',
            backgroundPosition: 'center',
          }}
        />
      </Grid>

      {/* Right Panel with Content */}
      <Grid
        item
        xs={12}
        sm={7}
        md={4}
        sx={{ display: 'flex', flexDirection: 'column', position: 'relative' }}
      >
        {/* Centralized Title with padding - following established pattern */}
        <Typography variant="h5" align="center" sx={{ p: 1.5 }}>
          Deliveroo Integration
        </Typography>

        {!isConnected ? (
          <OnboardingFlow
            locationId={locationId}
            onValidateIntegration={() => {
              setIsValidating(true)
              validateIntegration.mutate(
                {},
                {
                  onSuccess: () => {
                    setIsValidating(false)
                    connection.refetch()
                    toast.success('Integration validated successfully!')
                  },
                  onError: (error: any) => {
                    setIsValidating(false)
                    const errorMessage =
                      error?.response?.data?.error ||
                      'Integration validation failed. Please complete all steps in Deliveroo portal.'
                    toast.error(errorMessage)
                  },
                }
              )
            }}
            isValidating={isValidating}
          />
        ) : (
          <ConfigurationPanel connection={connection} />
        )}
      </Grid>
    </Grid>
  )
}

/**
 * Onboarding Flow Component
 */
const OnboardingFlow: FC<{
  locationId: string
  onValidateIntegration: () => void
  isValidating: boolean
}> = ({ locationId, onValidateIntegration, isValidating }) => {
  const [copied, setCopied] = useState(false)

  const handleCopyLocationId = async () => {
    try {
      await navigator.clipboard.writeText(locationId)
      setCopied(true)
      toast.success('Location ID copied to clipboard!')
      setTimeout(() => setCopied(false), 2000)
    } catch (error) {
      toast.error('Failed to copy Location ID')
    }
  }

  return (
    <Box sx={{ p: 2, maxWidth: 800, mx: 'auto' }}>
      {/* Documentation Link */}
      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ p: 2 }}>
          <Box display="flex" alignItems="center" mb={1.5}>
            <InfoIcon sx={{ mr: 1, color: 'primary.main', fontSize: 20 }} />
            <Typography variant="h6" component="h2" fontWeight="bold">
              Integration Guide
            </Typography>
          </Box>
          <Typography
            variant="body2"
            paragraph
            color="text.secondary"
            sx={{ mb: 2 }}
          >
            Click the link below and follow the steps to integrate with
            Deliveroo.
          </Typography>
          <Button
            variant="contained"
            size="medium"
            startIcon={<OpenInNewIcon />}
            onClick={() =>
              window.open(
                'https://docs.tryposhub.com/app-guides/marketplace/deliveroo#step-by-step-integration-guide',
                '_blank',
                'noopener,noreferrer'
              )
            }
            sx={{ px: 2, py: 1, fontSize: '0.9rem', fontWeight: 'bold' }}
          >
            View Integration Guide
          </Button>
        </CardContent>
      </Card>

      {/* Location ID Display */}
      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom fontWeight="bold">
            Your POS Hub Location ID
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            paragraph
            sx={{ mb: 2 }}
          >
            Copy this Location ID and use it when setting up the integration in
            your Deliveroo portal.
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              p: 1.5,
              bgcolor: 'grey.50',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'primary.main',
            }}
          >
            <Typography
              variant="body1"
              sx={{
                flexGrow: 1,
                fontFamily: 'monospace',
                fontWeight: 'bold',
                color: 'primary.main',
                fontSize: '0.9rem',
              }}
            >
              {locationId}
            </Typography>
            <Tooltip title={copied ? 'Copied!' : 'Copy Location ID'}>
              <IconButton
                onClick={handleCopyLocationId}
                color="primary"
                sx={{
                  bgcolor: 'primary.main',
                  color: 'white',
                  '&:hover': {
                    bgcolor: 'primary.dark',
                  },
                }}
              >
                <ContentCopyIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </CardContent>
      </Card>

      {/* Integration Complete Button */}
      <Card>
        <CardContent sx={{ p: 2, textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom fontWeight="bold">
            Complete Integration
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            paragraph
            sx={{ mb: 2 }}
          >
            Once you have completed all the steps in your Deliveroo portal,
            <br />
            please click the button below to validate the integration.
          </Typography>
          <Button
            variant="contained"
            size="medium"
            onClick={onValidateIntegration}
            disabled={isValidating}
            startIcon={
              isValidating ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <CheckCircleIcon />
              )
            }
            sx={{
              minWidth: 200,
              py: 1,
              px: 2,
              fontSize: '0.9rem',
              fontWeight: 'bold',
            }}
          >
            {isValidating ? 'Validating...' : 'Test Integration Status'}
          </Button>
        </CardContent>
      </Card>
    </Box>
  )
}

/**
 * Configuration Panel Component
 */
const ConfigurationPanel: FC<{ connection: any }> = ({ connection }) => {
  return (
    <Box sx={{ p: 3, maxWidth: 600, mx: 'auto' }}>
      <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
        <Typography variant="body1" fontWeight="bold">
          Integration successful! Your Deliveroo account is connected.
        </Typography>
      </Alert>

      <Card sx={{ boxShadow: 2 }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h5" gutterBottom fontWeight="bold">
            Configuration Settings
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            Configure your Deliveroo integration settings below.
          </Typography>

          {/* Placeholder for configuration options */}
          <Box
            sx={{
              p: 3,
              bgcolor: 'grey.50',
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'grey.200',
            }}
          >
            <Typography variant="body1" color="text.secondary" paragraph>
              Soon will be able to manage the following options here:
            </Typography>
            <Box component="ul" sx={{ pl: 2, mt: 1 }}>
              <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                Workload management (busy/quiet times)
              </Typography>
              <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                Menu sync settings
              </Typography>
              <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                Price management options
              </Typography>
              <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                Store status controls
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  )
}
